import {Template} from '../models/Template';
import {TemplatePropertySettings} from '../models/TemplatePropertySettings';
import {isNullOrEmpty} from './HelperUtils';
import {ObjectType} from '../models/ObjectType';
import {config} from '../init/config';
import {TemplateType} from '../models/TemplateType';
import {
    STORAGE_OBJECT_FULL_PATH,
    STORAGE_OBJECT_ID,
    STORAGE_OBJECT_NAME,
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_LABELS_SOFTWARE_PRODUCT_LOCALIZED_DATA,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_CREATOR,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_SCANNED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_SOFTWARE_PRODUCT_MAIN_DATA,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION,
    STORAGE_OBJECT_OBJECT_METADATA_WATERMARK,
    STORAGE_OBJECT_OBJECT_STATUS,
    STORAGE_OBJECT_OBJECT_TO_REMOVE,
    STORAGE_OBJECT_OBJECT_TYPE,
    STORAGE_OBJECT_PARTITION_KEY,
    STORAGE_OBJECT_PATH
} from '../init/storage_object_constants';
import {
    MODULE_AUDIT,
    MODULE_MALWARE_SCANNER,
    MODULE_SOFTWARE_PRODUCT,
    MODULE_TEMPLATES,
    ROLE_ACCESS_MOBILE_VIEW_ADMIN,
    ROLE_ACCESS_MOBILE_VIEW_EDITOR,
    ROLE_ACCESS_MOBILE_VIEW_USER
} from '../init/ui_configuration';
import {
    defaultModuleAudit,
    defaultModuleMalwareScanner,
    defaultModuleSoftwareProduct,
    defaultModuleTemplates,
    defaultRoleAccessMobileViewAdmin,
    defaultRoleAccessMobileViewEditor,
    defaultRoleAccessMobileViewUser
} from '../init/ui_configuration_defaults';

export const isActiveTemplate = (template: Template): boolean => {
    return ((template !== undefined) && template && template.isActive) as boolean;
};

export const getEmptyTemplatePropertySettings = (): TemplatePropertySettings => {
    let templatePropertySettings: TemplatePropertySettings = {} as TemplatePropertySettings;
    templatePropertySettings.isActive = templatePropertySettings.isActive ?? true;
    templatePropertySettings.isMandatory = templatePropertySettings.isMandatory ?? false;
    templatePropertySettings.isVisible = templatePropertySettings.isVisible ?? true;
    templatePropertySettings.isInheritable = templatePropertySettings.isInheritable ?? false;
    templatePropertySettings.defaultValue = templatePropertySettings?.defaultValue ?? '';

    return templatePropertySettings;
};

export const getTemplatePropertySettings = (propertyIdentifier: string, templatePropertySettingsMap: Map<string, TemplatePropertySettings>): TemplatePropertySettings => {
    if ((propertyIdentifier !== undefined) && propertyIdentifier && (!isNullOrEmpty(propertyIdentifier))
        && (templatePropertySettingsMap !== undefined) && templatePropertySettingsMap
        && (templatePropertySettingsMap.size > 0)) {
        return templatePropertySettingsMap.get(propertyIdentifier) ?? getEmptyTemplatePropertySettings();
    }

    return getEmptyTemplatePropertySettings();
};

export const getEmptyTemplatePropertySettingsMap = (): Map<string, TemplatePropertySettings> => {
    return new Map() as Map<string, TemplatePropertySettings>;

};

export const getDefaultTemplateUiConfigurationPropertySettingsMap = (): Map<string, TemplatePropertySettings> => {
    let templateSettings = [] as [string, TemplatePropertySettings][];
    let templatePropertySettings = getEmptyTemplatePropertySettings();
    templatePropertySettings.isActive = false;

    //Not Visible elements by default
    if (!defaultModuleTemplates) {
        templateSettings.push([MODULE_TEMPLATES, templatePropertySettings]);
    }

    if (!defaultModuleSoftwareProduct) {
        templateSettings.push([MODULE_SOFTWARE_PRODUCT, templatePropertySettings]);
    }

    if (!defaultModuleAudit) {
        templateSettings.push([MODULE_AUDIT, templatePropertySettings]);
    }

    if (!defaultModuleMalwareScanner) {
        templateSettings.push([MODULE_MALWARE_SCANNER, templatePropertySettings]);
    }

    if (!defaultRoleAccessMobileViewAdmin) {
        templateSettings.push([ROLE_ACCESS_MOBILE_VIEW_ADMIN, templatePropertySettings]);
    }

    if (!defaultRoleAccessMobileViewEditor) {
        templateSettings.push([ROLE_ACCESS_MOBILE_VIEW_EDITOR, templatePropertySettings]);
    }

    if (!defaultRoleAccessMobileViewUser) {
        templateSettings.push([ROLE_ACCESS_MOBILE_VIEW_USER, templatePropertySettings]);
    }

    return new Map(templateSettings) as Map<string, TemplatePropertySettings>;
};

export const getDefaultTemplateMetadataPropertySettingsMap = (): Map<string, TemplatePropertySettings> => {
    //Setup only mandatory DB fields. Used as fallback validation rule set for mandatory fields when no templates are defined
    let templateSettings = Array.from(getDefaultTemplateUiConfigurationPropertySettingsMap()) as [string, TemplatePropertySettings][];
    let templatePropertySettings = getEmptyTemplatePropertySettings();
    templatePropertySettings.isMandatory = true;

    //StorageObjectMainData
    templateSettings.push([STORAGE_OBJECT_ID, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_PARTITION_KEY, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_PATH, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_FULL_PATH, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_NAME, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_TYPE, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_TO_REMOVE, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_STATUS, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings]);

    //StorageObjectMetadataData
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_DATE_SCANNED, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_CREATOR, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_VERSION, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_METADATA_WATERMARK, templatePropertySettings]);

    //StorageObjectLocalizedData
    templateSettings.push([STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings]);
    templateSettings.push([STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings]);

    return new Map(templateSettings) as Map<string, TemplatePropertySettings>;
};

export const getUiConfigurationTemplates = (templates: Template[]): Template[] => {
    if ((templates !== undefined) && templates && (templates.length > 0)) {
        let applicableTemplateType: TemplateType[] = [TemplateType.UI_CONFIGURATION_SETTINGS, TemplateType.UI_COMPONENT_SETTINGS];
        return templates
            .filter(template => (isActiveTemplate(template) && (applicableTemplateType.includes(template.templateType)))) ?? [] as Template[];
    }

    return [] as Template[];
};

export const getStorageObjectTemplates = (templates: Template[]): Template[] => {
    if ((templates !== undefined) && templates && (templates.length > 0)) {
        let applicableTemplateType: TemplateType[] = [TemplateType.STORAGE_OBJECT_FOLDER, TemplateType.STORAGE_OBJECT_FILE];
        return templates
            .filter(template => (isActiveTemplate(template) && (applicableTemplateType.includes(template.templateType)))) ?? [] as Template[];
    }

    return [] as Template[];
};

export const getUiApplicableTemplates = (): Template[] => {
    if (config.DLS_UI_CONFIGURATION_TEMPLATES.length === 0) {
        return [] as Template[];
    }

    return config.DLS_UI_CONFIGURATION_TEMPLATES;
};

export const getApplicableTemplates = (objectType: ObjectType): Template[] => {
    if ((config.DLS_STORAGE_OBJECT_TEMPLATES.length === 0) && (config.DLS_UI_CONFIGURATION_TEMPLATES.length === 0)) {
        return [] as Template[];
    }

    let applicableTemplates: Template[] = [];
    if (config.DLS_UI_CONFIGURATION_TEMPLATES.length > 0) {
        applicableTemplates = applicableTemplates.concat(config.DLS_UI_CONFIGURATION_TEMPLATES);
    }

    if (config.DLS_STORAGE_OBJECT_TEMPLATES.length > 0) {
        let applicableTemplateType: TemplateType;
        switch (objectType) {
            case ObjectType.FOLDER:
                applicableTemplateType = TemplateType.STORAGE_OBJECT_FOLDER;
                break;
            case ObjectType.FILE:
                applicableTemplateType = TemplateType.STORAGE_OBJECT_FILE;
                break;
            default:
                break;
        }

        let storageObjectTemplates = config.DLS_STORAGE_OBJECT_TEMPLATES
            .filter(template => (isActiveTemplate(template) && (applicableTemplateType === template.templateType))) ?? [] as Template[];
        if (storageObjectTemplates.length > 0) {
            applicableTemplates = applicableTemplates.concat(storageObjectTemplates);
        }
    }

    return applicableTemplates;
};

export const applyTemplateMetadataPropertySettings = (isParentActive: boolean, isParentVisible: boolean, templatePropertySettings: Map<string, TemplatePropertySettings>,
                                                      applicablePropertySettings: Map<string, TemplatePropertySettings>): Map<string, TemplatePropertySettings> => {
    if ((templatePropertySettings === undefined) || (!templatePropertySettings)) {
        templatePropertySettings = getEmptyTemplatePropertySettingsMap();
    }

    if ((applicablePropertySettings === undefined) || (!applicablePropertySettings) || (applicablePropertySettings.size === 0)) {
        return templatePropertySettings;
    }

    let tempSettings: TemplatePropertySettings;
    let keys = Object.keys(Object.fromEntries(applicablePropertySettings));
    keys.map(key => {
        tempSettings = applicablePropertySettings.get(key) ?? getEmptyTemplatePropertySettings();
        if (!templatePropertySettings.has(key)) {
            tempSettings.isActive = isParentActive && tempSettings.isActive && tempSettings.isActive;
            tempSettings.isVisible = isParentVisible && tempSettings.isVisible && tempSettings.isVisible;
            templatePropertySettings.set(key, tempSettings);
        } else {
            let combinedTempSettings = templatePropertySettings.get(key) ?? getEmptyTemplatePropertySettings();
            combinedTempSettings.isActive = isParentActive && combinedTempSettings.isActive && tempSettings.isActive;
            combinedTempSettings.isMandatory = tempSettings.isMandatory ? tempSettings.isMandatory : combinedTempSettings.isMandatory;
            combinedTempSettings.isVisible = isParentVisible && combinedTempSettings.isVisible && tempSettings.isVisible;
            combinedTempSettings.isInheritable = tempSettings.isInheritable ? tempSettings.isInheritable : combinedTempSettings.isInheritable;
            combinedTempSettings.defaultValue = combinedTempSettings.defaultValue ?? (tempSettings?.defaultValue ?? '');
            templatePropertySettings.set(key, combinedTempSettings);
        }
    });

    return templatePropertySettings;
};


export const mergeTemplatePropertyMaps = (templatePropertySettings: Map<string, TemplatePropertySettings>,
                                          templatePropertySettingsToMerge: Map<string, TemplatePropertySettings>): Map<string, TemplatePropertySettings> => {
    if (!templatePropertySettingsToMerge) {
        return templatePropertySettings;
    }

    let temp = new Map(Object.entries(templatePropertySettingsToMerge));
    let keys = Object.keys(Object.fromEntries(temp));
    keys.map(key => {
        templatePropertySettings.set(key, temp.get(key) as TemplatePropertySettings);
    });

    return templatePropertySettings;
};

export const getApplicableTemplateUiConfigurationPropertySettingsMap = (): Map<string, TemplatePropertySettings> => {
    let templates: Template[] = getUiApplicableTemplates();
    let defaultTemplateSettings = getDefaultTemplateUiConfigurationPropertySettingsMap();
    if (templates.length === 0) {
        return defaultTemplateSettings;
    }

    let templatePropertySettings: Map<string, TemplatePropertySettings> = getEmptyTemplatePropertySettingsMap();
    templates.forEach(template => {
        templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActiveTemplate(template), true,
            templatePropertySettings, new Map(Object.entries(template.uiConfigurationSettings ?? getEmptyTemplatePropertySettingsMap()))));
        templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActiveTemplate(template), true,
            templatePropertySettings, new Map(Object.entries(template.uiComponentSettings ?? getEmptyTemplatePropertySettingsMap()))));
        if (templatePropertySettings.size === 0) {
            return;
        }
    });

    let keys = Object.keys(Object.fromEntries(defaultTemplateSettings));
    keys.filter(key => (!templatePropertySettings.has(key)))
        .map(key => templatePropertySettings.set(key, defaultTemplateSettings.get(key) as TemplatePropertySettings));

    return templatePropertySettings;
};

export const getApplicableTemplateMetadataPropertySettingsMap = (objectType: ObjectType, templateId?: string): Map<string, TemplatePropertySettings> => {
    let templates: Template[] = getApplicableTemplates(objectType);
    let defaultTemplateSettings = getDefaultTemplateMetadataPropertySettingsMap();
    if (templates.length === 0) {
        return defaultTemplateSettings;
    }

    let templatePropertySettings: Map<string, TemplatePropertySettings> = getApplicableTemplateUiConfigurationPropertySettingsMap();
    let templateScope = templates
        .filter(template => (((templateId !== undefined) && (!isNullOrEmpty(templateId))) ? (template.id === templateId) : true)) ?? templatePropertySettings;

    if (templateScope && templateScope.length === 0) {
        templateScope = templates;
    }

    templateScope.forEach(template => {
        templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActiveTemplate(template), true,
            templatePropertySettings, new Map(Object.entries(template.storageObjectSettings ?? getEmptyTemplatePropertySettingsMap()))));
        if (templatePropertySettings.size === 0) {
            return;
        }

        let isActive = isActiveComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings);
        let isVisible = isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings);
        if (isActive) {
            templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActive, isVisible,
                templatePropertySettings, new Map(Object.entries(template.storageObjectMetadataSettings ?? getEmptyTemplatePropertySettingsMap()))));

            isActive = isActive && isActiveComponent(STORAGE_OBJECT_OBJECT_METADATA_SOFTWARE_PRODUCT_MAIN_DATA, templatePropertySettings);
            isVisible = isVisible && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_SOFTWARE_PRODUCT_MAIN_DATA, templatePropertySettings);
            if (isActive) {
                templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActive, isVisible,
                    templatePropertySettings, new Map(Object.entries(template.softwareProductMainDataSettings ?? getEmptyTemplatePropertySettingsMap()))));
            }
        }

        isActive = isActiveComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings);
        isVisible = isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings);
        if (isActive) {
            templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActive, isVisible,
                templatePropertySettings, new Map(Object.entries(template.storageObjectLocalizedDataSettings ?? getEmptyTemplatePropertySettingsMap()))));

            isActive = isActive && isActiveComponent(STORAGE_OBJECT_OBJECT_LABELS_SOFTWARE_PRODUCT_LOCALIZED_DATA, templatePropertySettings);
            isVisible = isVisible && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_SOFTWARE_PRODUCT_LOCALIZED_DATA, templatePropertySettings);
            if (isActive) {
                templatePropertySettings = mergeTemplatePropertyMaps(templatePropertySettings, applyTemplateMetadataPropertySettings(isActive, isVisible,
                    templatePropertySettings, new Map(Object.entries(template.softwareProductLocalizedDataSettings ?? getEmptyTemplatePropertySettingsMap()))));
            }
        }
    });

    let keys = Object.keys(Object.fromEntries(defaultTemplateSettings));
    keys.filter(key => (!templatePropertySettings.has(key)))
        .map(key => templatePropertySettings.set(key, defaultTemplateSettings.get(key) as TemplatePropertySettings));

    return templatePropertySettings;
};

export const getApplicableTemplateMetadataPropertySettingsMapByObjectType = (propertyIdentifier: string, objectType: ObjectType, templateId?: string): Map<string, TemplatePropertySettings> => {
    let templatePropertySettings: Map<string, TemplatePropertySettings> = getEmptyTemplatePropertySettingsMap();
    if ((propertyIdentifier !== undefined) && propertyIdentifier && (!isNullOrEmpty(propertyIdentifier))) {
        return templatePropertySettings;
    }

    let applicableTemplatePropertySettings = getApplicableTemplateMetadataPropertySettingsMap(objectType, templateId);
    templatePropertySettings.set(propertyIdentifier, applicableTemplatePropertySettings.get(propertyIdentifier) ?? getEmptyTemplatePropertySettings());

    return templatePropertySettings;
};

export const getApplicableTemplateMetadataPropertySettingsByObjectType = (propertyIdentifier: string, objectType: ObjectType, templateId?: string): TemplatePropertySettings[] => {
    return new Array(getApplicableTemplateMetadataPropertySettingsMapByObjectType(propertyIdentifier, objectType, templateId).get(propertyIdentifier) ?? getEmptyTemplatePropertySettings()) as TemplatePropertySettings[];
};

export const getApplicableTemplateMetadataPropertySettings = (propertyIdentifier: string, templatePropertySettings: Map<string, TemplatePropertySettings>): TemplatePropertySettings[] => {
    return new Array(templatePropertySettings.get(propertyIdentifier) ?? getEmptyTemplatePropertySettings()) as TemplatePropertySettings[];
};

export const isApplicableTemplateSettings = (propertyIdentifier: string, templatePropertySettings: TemplatePropertySettings): boolean => {
    return ((propertyIdentifier !== undefined) && propertyIdentifier && (!isNullOrEmpty(propertyIdentifier)) && templatePropertySettings) as boolean;
};

export const isActiveTemplateSettings = (propertyIdentifier: string, templatePropertySettings: TemplatePropertySettings): boolean => {
    return (isApplicableTemplateSettings(propertyIdentifier, templatePropertySettings)
        && (templatePropertySettings.isActive !== undefined) && templatePropertySettings.isActive) as boolean;
};

export const isMandatoryTemplateSetting = (propertyIdentifier: string, templatePropertySettings: TemplatePropertySettings): boolean => {
    return (isApplicableTemplateSettings(propertyIdentifier, templatePropertySettings)
        && (templatePropertySettings.isMandatory !== undefined) && templatePropertySettings.isMandatory) as boolean;
};

export const isVisibleTemplateSetting = (propertyIdentifier: string, templatePropertySettings: TemplatePropertySettings): boolean => {
    return (isApplicableTemplateSettings(propertyIdentifier, templatePropertySettings)
        && (templatePropertySettings.isVisible !== undefined) && templatePropertySettings.isVisible) as boolean;
};

export const isInheritableTemplateSetting = (propertyIdentifier: string, templatePropertySettings: TemplatePropertySettings): boolean => {
    return (isApplicableTemplateSettings(propertyIdentifier, templatePropertySettings)
        && (templatePropertySettings.isInheritable !== undefined) && templatePropertySettings.isInheritable) as boolean;
};

export const getDefaultValueOfTemplateSetting = (propertyIdentifier: string, templatePropertySettings: TemplatePropertySettings): string => {
    if (isApplicableTemplateSettings(propertyIdentifier, templatePropertySettings)
        && (templatePropertySettings.defaultValue !== undefined) && (!isNullOrEmpty(templatePropertySettings.defaultValue))) {
        return templatePropertySettings.defaultValue.trim();
    }

    return '';
};

export const isActiveComponent = (propertyIdentifier: string, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    if ((templatePropertySettings === undefined) || (!templatePropertySettings) || (templatePropertySettings.size === 0)) {
        return true;
    }

    let activeTemplatePropertySettings = getApplicableTemplateMetadataPropertySettings(propertyIdentifier, templatePropertySettings)
        .filter(templateSettings => (isActiveTemplateSettings(propertyIdentifier, templateSettings))) ?? [] as TemplatePropertySettings[];

    return (activeTemplatePropertySettings.length > 0) as boolean;
};

export const isVisibleComponent = (propertyIdentifier: string, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    if ((templatePropertySettings === undefined) || (!templatePropertySettings) || (templatePropertySettings.size === 0)) {
        return true;
    }

    let activeTemplatePropertySettings = getApplicableTemplateMetadataPropertySettings(propertyIdentifier, templatePropertySettings)
        .filter(templateSettings => (isActiveTemplateSettings(propertyIdentifier, templateSettings)))
        .filter(templateSettings => (isVisibleTemplateSetting(propertyIdentifier, templateSettings))) ?? [] as TemplatePropertySettings[];

    return (activeTemplatePropertySettings.length > 0) as boolean;
};

export const isMandatoryComponent = (propertyIdentifier: string, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    if ((templatePropertySettings === undefined) || (!templatePropertySettings) || (templatePropertySettings.size === 0)) {
        return false;
    }

    let activeTemplatePropertySettings = getApplicableTemplateMetadataPropertySettings(propertyIdentifier, templatePropertySettings)
        .filter(templateSettings => (isActiveTemplateSettings(propertyIdentifier, templateSettings)))
        .filter(templateSettings => (isMandatoryTemplateSetting(propertyIdentifier, templateSettings))) ?? [] as TemplatePropertySettings[];

    return (activeTemplatePropertySettings.length > 0) as boolean;
};

export const isInheritableComponent = (propertyIdentifier: string, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    if ((templatePropertySettings === undefined) || (!templatePropertySettings) || (templatePropertySettings.size === 0)) {
        return false;
    }

    let activeTemplatePropertySettings = getApplicableTemplateMetadataPropertySettings(propertyIdentifier, templatePropertySettings)
        .filter(templateSettings => (isActiveTemplateSettings(propertyIdentifier, templateSettings)))
        .filter(templateSettings => (isInheritableTemplateSetting(propertyIdentifier, templateSettings))) ?? [] as TemplatePropertySettings[];

    return (activeTemplatePropertySettings.length > 0) as boolean;
};

export const getDefaultValueOfComponent = (propertyIdentifier: string, templatePropertySettings: Map<string, TemplatePropertySettings>): string => {
    if ((templatePropertySettings === undefined) || (!templatePropertySettings) || (templatePropertySettings.size === 0)) {
        return '';
    }

    let activeTemplatePropertySettings = getApplicableTemplateMetadataPropertySettings(propertyIdentifier, templatePropertySettings)
        .filter(templateSettings => (isActiveTemplateSettings(propertyIdentifier, templateSettings)))
        .filter(templateSettings => (('' !== getDefaultValueOfTemplateSetting(propertyIdentifier, templateSettings)))) ?? [] as TemplatePropertySettings[];

    if (activeTemplatePropertySettings.length > 0) {
        return getDefaultValueOfTemplateSetting(propertyIdentifier, activeTemplatePropertySettings.values().next().value);
    }

    return '';
};
